/* eslint-disable react-refresh/only-export-components */
/**
 * Orchestration hook for background port detection with interactive toasts.
 * When a sandbox has the `workload: ai-coding` label and is running,
 * this hook monitors for newly listening TCP ports and offers to expose them
 * via an interactive toast with Entra ID auth and an inline email input.
 *
 * @module hooks/usePortDetectorToast
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import { usePortDetector, type DetectedPort } from './usePortDetector';
import type { SandboxResponse, PortProtocol } from '@/types';
import type { useAddPort } from './useSandboxes';

// ---------------------------------------------------------------------------
// Theme-aware color tokens
// ---------------------------------------------------------------------------

/**
 * Reads the current theme from the DOM (`dark` class on `<html>`) and returns
 * the appropriate color tokens. This avoids the stale-state issue of the
 * `useDarkMode` hook (which creates independent state per consumer) and reads
 * the single source of truth directly.
 */
function getThemeColors() {
  const isDark = document.documentElement.classList.contains('dark');
  return isDark
    ? {
        bg: '#1e1e1e',
        text: '#fff',
        textSecondary: '#ccc',
        shadow: '0 8px 32px rgba(0,0,0,0.6), 0 2px 8px rgba(0,0,0,0.4)',
        border: '#4b5563',
        accentBlue: '#3b82f6',
        accentGreen: '#22c55e',
        inputBg: '#2a2a2a',
        inputBorder: '#555',
        inputText: '#fff',
        dismissBorder: '#555',
        dismissText: '#ccc',
        linkColor: '#60a5fa',
        linkHover: '#93bbfd',
        checkColor: '#4ade80',
      }
    : {
        bg: '#f8fafc',
        text: '#1a1a1a',
        textSecondary: '#555',
        shadow: '0 8px 32px rgba(0,0,0,0.16), 0 2px 8px rgba(0,0,0,0.08)',
        border: '#e2e8f0',
        accentBlue: '#3b82f6',
        accentGreen: '#16a34a',
        inputBg: '#f5f5f5',
        inputBorder: '#d4d4d4',
        inputText: '#1a1a1a',
        dismissBorder: '#d4d4d4',
        dismissText: '#555',
        linkColor: '#2563eb',
        linkHover: '#1d4ed8',
        checkColor: '#16a34a',
      };
}

// ---------------------------------------------------------------------------
// PortDetectedToast — small component rendered inside toast.custom()
// ---------------------------------------------------------------------------

/**
 * Props for the inline port-detected toast component.
 */
interface PortDetectedToastProps {
  toastId: string;
  detectedPort: DetectedPort;
  defaultEmail: string;
  sandboxId: string;
  addPortMutation: ReturnType<typeof useAddPort>;
  onDismiss: () => void;
}

/**
 * Interactive toast that offers to expose a detected port with Entra ID auth.
 * Manages its own email and loading state internally so it works inside
 * `toast.custom()` which renders a static React element.
 */
function PortDetectedToast({
  toastId,
  detectedPort,
  defaultEmail,
  sandboxId,
  addPortMutation,
  onDismiss,
}: PortDetectedToastProps) {
  const [email, setEmail] = useState(defaultEmail);
  const [isExposing, setIsExposing] = useState(false);
  const colors = getThemeColors();

  const handleExpose = () => {
    if (!email.trim()) return;
    setIsExposing(true);

    addPortMutation.mutate(
      {
        sandboxId,
        port: detectedPort.port,
        auth: {
          entraId: {
            enabled: true,
            emails: [email.trim()],
            emailSuffixes: [],
            objectIds: [],
            tenantIds: [],
          },
        },
        protocol: 'Http' as PortProtocol,
      },
      {
        onSuccess: (data) => {
          toast.dismiss(toastId);
          const url = data?.url;
          const successColors = getThemeColors();
          toast.custom(
            (t) => (
              <div
                style={{
                  background: successColors.bg,
                  color: successColors.text,
                  borderRadius: '8px',
                  padding: '14px 16px',
                  maxWidth: '380px',
                  width: '100%',
                  boxShadow: successColors.shadow,
                  border: `1px solid ${successColors.border}`,
                  borderLeft: `4px solid ${successColors.accentGreen}`,
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  fontSize: '14px',
                  opacity: t.visible ? 1 : 0,
                  transition: 'opacity 150ms ease-in-out',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: url ? '8px' : '0',
                  }}
                >
                  <span style={{ color: successColors.checkColor, fontSize: '16px' }}>
                    &#x2705;
                  </span>
                  <span style={{ fontWeight: 600 }}>Port {detectedPort.port} exposed!</span>
                </div>
                {url && (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'block',
                      color: successColors.linkColor,
                      fontSize: '13px',
                      textDecoration: 'underline',
                      wordBreak: 'break-all',
                      cursor: 'pointer',
                      marginBottom: '10px',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = successColors.linkHover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = successColors.linkColor;
                    }}
                  >
                    {url}
                  </a>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => toast.dismiss(t.id)}
                    style={{
                      padding: '5px 12px',
                      borderRadius: '4px',
                      border: `1px solid ${successColors.dismissBorder}`,
                      background: 'transparent',
                      color: successColors.dismissText,
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            ),
            { duration: Infinity, id: `port-exposed-${detectedPort.port}` }
          );
        },
        onError: () => {
          setIsExposing(false);
          // useAddPort already shows an error toast via showErrorToast
        },
      }
    );
  };

  const handleDismiss = () => {
    onDismiss();
    toast.dismiss(toastId);
  };

  return (
    <div
      style={{
        background: colors.bg,
        color: colors.text,
        borderRadius: '8px',
        padding: '14px 16px',
        maxWidth: '380px',
        width: '100%',
        boxShadow: colors.shadow,
        border: `1px solid ${colors.border}`,
        borderLeft: `4px solid ${colors.accentBlue}`,
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: '14px',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <span style={{ fontSize: '16px' }}>&#x1F50C;</span>
        <span style={{ fontWeight: 600 }}>
          Port {detectedPort.port} detected
          {detectedPort.process && detectedPort.process !== '-' ? ` (${detectedPort.process})` : ''}
        </span>
      </div>

      {/* Description */}
      <div style={{ color: colors.textSecondary, marginBottom: '10px', fontSize: '13px' }}>
        Would you like to expose this port?
        <br />
        Auth: Entra ID
      </div>

      {/* Email input */}
      <div style={{ marginBottom: '10px' }}>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email for Entra ID auth"
          disabled={isExposing}
          style={{
            width: '100%',
            padding: '6px 10px',
            borderRadius: '4px',
            border: `1px solid ${colors.inputBorder}`,
            background: colors.inputBg,
            color: colors.inputText,
            fontSize: '13px',
            outline: 'none',
            boxSizing: 'border-box',
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = '#3b82f6';
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = colors.inputBorder;
          }}
        />
      </div>

      {/* Buttons */}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button
          onClick={handleDismiss}
          disabled={isExposing}
          style={{
            padding: '5px 12px',
            borderRadius: '4px',
            border: `1px solid ${colors.dismissBorder}`,
            background: 'transparent',
            color: colors.dismissText,
            fontSize: '13px',
            cursor: isExposing ? 'not-allowed' : 'pointer',
            opacity: isExposing ? 0.5 : 1,
          }}
        >
          Dismiss
        </button>
        <button
          onClick={handleExpose}
          disabled={!email.trim() || isExposing}
          style={{
            padding: '5px 12px',
            borderRadius: '4px',
            border: 'none',
            background: !email.trim() || isExposing ? '#1e40af' : '#3b82f6',
            color: '#fff',
            fontSize: '13px',
            fontWeight: 500,
            cursor: !email.trim() || isExposing ? 'not-allowed' : 'pointer',
            opacity: !email.trim() || isExposing ? 0.6 : 1,
          }}
        >
          {isExposing ? 'Exposing...' : 'Expose Port'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// usePortDetectorToast — the orchestration hook
// ---------------------------------------------------------------------------

interface UsePortDetectorToastOptions {
  sandboxId: string;
  sandbox: SandboxResponse | undefined;
  isRunning: boolean;
  addPortMutation: ReturnType<typeof useAddPort>;
}

/**
 * Background port detection orchestrator.
 *
 * Activates only when:
 *  1. The sandbox has `labels.workload === 'ai-coding'`
 *  2. The sandbox is running
 *
 * Shows an interactive toast for each newly detected port that is not
 * already exposed on the sandbox, allowing the user to expose it with
 * Entra ID auth in one click.
 */
export function usePortDetectorToast({
  sandboxId,
  sandbox,
  isRunning,
  addPortMutation,
}: UsePortDetectorToastOptions): void {
  const { user } = useAuth();
  const defaultEmail = (user?.email ?? '').replace(/\+microsoft(?=@)/i, '');

  // Track which ports we have already shown a toast for (so we don't re-toast)
  const offeredPortsRef = useRef<Set<number>>(new Set());

  const shouldActivate = isRunning && sandbox?.labels?.['workload'] === 'ai-coding';

  const { detectedPorts } = usePortDetector(sandboxId, shouldActivate);

  // Clear offered ports when deactivating so we re-detect on next activation
  const prevActiveRef = useRef(shouldActivate);
  useEffect(() => {
    if (prevActiveRef.current && !shouldActivate) {
      offeredPortsRef.current.clear();
    }
    prevActiveRef.current = shouldActivate;
  }, [shouldActivate]);

  // Callback to mark a port as dismissed (user clicked Dismiss)
  const handleDismiss = useCallback((port: number) => {
    offeredPortsRef.current.add(port);
  }, []);

  // Compare detected ports against exposed + already-offered and show toasts
  useEffect(() => {
    if (!shouldActivate || detectedPorts.length === 0) return;

    // Build set of already-exposed port numbers
    const exposedPorts = new Set<number>((sandbox?.ports ?? []).map((p) => p.port));

    for (const dp of detectedPorts) {
      // Skip if already exposed or already offered
      if (exposedPorts.has(dp.port) || offeredPortsRef.current.has(dp.port)) {
        continue;
      }

      // Mark as offered so we don't toast again
      offeredPortsRef.current.add(dp.port);

      // Show interactive toast
      toast.custom(
        (t) => (
          <PortDetectedToast
            toastId={t.id}
            detectedPort={dp}
            defaultEmail={defaultEmail}
            sandboxId={sandboxId}
            addPortMutation={addPortMutation}
            onDismiss={() => handleDismiss(dp.port)}
          />
        ),
        { duration: Infinity, id: `port-detect-${dp.port}` }
      );
    }
  }, [
    shouldActivate,
    detectedPorts,
    sandbox?.ports,
    defaultEmail,
    sandboxId,
    addPortMutation,
    handleDismiss,
  ]);
}
